import { useOktaAuth } from "@okta/okta-react";
import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AttributesTooltip, Layout, LoadingOverlayIndicator, TermsAndConditionsModal } from "../components";
import notifications from "../container/notifications";
import { KEY_ACTIVE_REGISTRY_ID, useAppContext } from "../context/app-context";
import { useAccountInfo } from "../hooks/use-account-info";
import { useOktaEnabledRegistriesWatch } from "../hooks/use-okta-enabled-registries-watch";
import { useOktaRoles } from "../hooks/use-okta-roles";
import { isOnboardingOrRetryUser } from "../lib/account-service-helpers";
import { getDefaultActiveRegistry } from "../lib/active-registry-helper";
import { useModalContext } from "../modal-provider";
import { Home } from "../pages/public/home";
import { AccountStatus, RegistryLinkStatus, useFewAccountDetailsQuery } from "../types/generated/graphql";
import { flattenPrivateRoutes, PrivateRoute } from "./routes/private-routes";
import { PublicRoute, publicRoutes } from "./routes/public-routes";
const Onboarding = lazy(() => import("../pages/public/signup/onboarding/onboarding"));

export const AppRouter = () => {
  useOktaEnabledRegistriesWatch();
  const { authState } = useOktaAuth();
  const { closeModal } = useModalContext();
  const { userRoles } = useOktaRoles();
  const location = useLocation();

  const { userAccountInfo, userSelections, setUserAccountInfo, setUserSelections } = useAppContext();
  const isAuthenticated = authState?.isAuthenticated;
  const accountInfo = useAccountInfo({
    skipQuery: !isAuthenticated,
  });

  useEffect(() => {
    let accountType;

    if (
      accountInfo.dataReady &&
      !accountInfo.linkedRegistries?.length &&
      accountInfo.status &&
      [AccountStatus.Active, AccountStatus.AwaitingVerification, AccountStatus.AwaitingReverification].includes(
        accountInfo.status,
      )
    ) {
      throw new Error("No linked registries found for the user.");
    }

    if (accountInfo.dataReady && !userSelections) {
      const lastSelectedUserRegistryId = localStorage.getItem(KEY_ACTIVE_REGISTRY_ID);
      const lastSelectedUserRegistry = accountInfo.linkedRegistries?.find(
        (linkedRegistry) => linkedRegistry.registryId === lastSelectedUserRegistryId,
      );
      const activeRegistry =
        lastSelectedUserRegistry?.status === RegistryLinkStatus.Registered
          ? lastSelectedUserRegistry
          : getDefaultActiveRegistry(accountInfo.linkedRegistries);

      accountType = activeRegistry?.accountType;

      setUserSelections({
        registry: activeRegistry,
      });
    }

    if (accountInfo.dataReady && !userAccountInfo?.loaded) {
      setUserAccountInfo({
        id: accountInfo.id,
        tenantId: accountInfo.tenantId,
        firstName: accountInfo.firstName,
        lastName: accountInfo.lastName,
        email: accountInfo.email,
        status: accountInfo.status,
        roles: userRoles,
        accountType,
        linkedRegistries: accountInfo.linkedRegistries,
        loaded: true,
      });
    }
  }, [accountInfo, userRoles, userAccountInfo, userSelections, setUserAccountInfo, setUserSelections]);

  // Closes the modal whenever the route (pathname) changes
  useEffect(() => {
    closeModal();
  }, [location.pathname, closeModal]);

  return (
    <Routes>
      <Route path="/" element={<RootRouteRender />} />
      {publicRoutes.map((route) => (
        <Route path={route.path} key={route.path} element={<PublicRoute>{route.render()}</PublicRoute>} />
      ))}
      {flattenPrivateRoutes.map((route) => (
        <Route
          path={route.path}
          key={route.path}
          element={
            <PrivateRoute guard={route.guard}>
              <Layout>
                {/* Fallback to match layout to avoid flickering */}
                <Suspense fallback={<Layout></Layout>}>{route.render()}</Suspense>
                <AttributesTooltip />
              </Layout>
            </PrivateRoute>
          }
        />
      ))}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export const RootRouteRender = () => {
  const { authState } = useOktaAuth();

  const { data, loading, error } = useFewAccountDetailsQuery({
    skip: !authState?.isAuthenticated,
    // setting fetchPolicy to "no-cache" fixes the bug in the card TX-2614, as a side-effect causes 2 API calls to fetch account when logging in
    fetchPolicy: "no-cache",
    onError: (error) => {
      error instanceof Error &&
        notifications.error({
          description: error?.message,
        });
    },
  });

  if (error) {
    throw error;
  }

  //If not authenticated show Login Page
  if (!authState?.isAuthenticated) return <Home />;

  //If authenticated => query is hit and loading is true, Show loading Overlay
  if (loading) return <LoadingOverlayIndicator />;

  //If account status is onboarding or retry, go To Signup
  if (isOnboardingOrRetryUser(data?.account?.status))
    return (
      <Suspense fallback={<LoadingOverlayIndicator />}>
        <Onboarding />
      </Suspense>
    );

  // If User has not accepted the terms and conditions
  if (
    !data?.account?.termsAcceptedDate ||
    !data?.account?.rulesAcceptedDate ||
    !data?.account?.privacyPolicyAcceptedDate
  ) {
    return <TermsAndConditionsModal />;
  }

  //Else Navigate to Market
  return <Navigate to={"/market/spot-trade"} replace />;
};
